import { invalidateSportsnewFixtureDetailRequest, isSportsnewFixtureDetailGenerationCurrent } from '../_helpers/sportsnewFixtureDetailLifecycle';
import { recordSportsnewRestOdds, sportsnewRealtimeOddsFromFixturePatch } from '../_helpers/sportsnewDetailRealtime';
import { useCallback, useEffect } from 'react';

import { fxId } from '../_helpers/sportsnewListPatch';
import {
  beginSportsnewDetailRecovery,
  invalidateSportsnewDetailRequest,
  isSportsnewDetailRecoveryCurrent,
  isSportsnewDetailUnavailable,
  shouldClearSportsnewActiveDetail,
} from '../_helpers/sportsnewDetailRequestLifecycle';
import {
  canPaintSportsnewDetailCache,
  createSportsnewDetailRequestToken,
  isSportsnewDetailRequestCurrent,
  sportsnewDetailResponseMatchesRequest,
} from '../_helpers/sportsnewDetailRequestFence';
import { renderSportsnewDetailMarketsProgressively } from '../_helpers/sportsnewDetailProgressiveLoad';
import { recoverFixtureSnapshotPayload } from '../_helpers/sportsnewContextConfig';
import {
  clearDetailClickMark,
  markDetailClick,
  markPerf,
  measureDetailHeader,
  measureFirstMarket,
  measureFullDetailLoaded,
} from '../_helpers/sportsnewPerf';

export const useSportsnewFixtureDetail = ({
  frameDeliveryRef,
  SportsnewService,
  recoverFixtureDetailSingleFlight,
  sessionOwnerRef,
  fixtureRecoveryInFlightRef,
  selectionsRef,
  detailAbortControllerRef,
  detailCacheRef,
  detailFixtureIdRef,
  detailRealtimeLedgerRef,
  detailRecoveryGenerationRef,
  detailRequestSeqRef,
  detailLifecycleGenerationRef,
  displayScope,
  displayScopeRef,
  fixtureSequenceRef,
  fixturesRef,
  latestDetailRequestRef,
  marketChunkRafRef,
  mode,
  modeRef,
  operatorSuspendedFixtureIdsRef,
  reconcileRestDetailMarkets,
  setDetailFixture,
  setDetailFixtureId,
  setDetailLoading,
  setDetailMarkets,
}) => {
  /* ─────────────────────────────────────────────────────
     API — fixture detail
  ───────────────────────────────────────────────────── */

  // Prematch progressive/chunked render: a heavy fixture (soccer/basketball
  // with 100+ markets) is not set into state in one giant call — the
  // first chunk (the first visible market group) renders immediately, and
  // the rest streams in across animation frames so the main thread never
  // blocks on one huge re-render. LIVE and cache-hit background revalidation
  // bypass this renderer and apply a reconciled array atomically.
  const renderMarketsProgressively = useCallback((requestToken, markets, measureFromClick) => {
    renderSportsnewDetailMarketsProgressively({
      request: requestToken,
      markets,
      measureFromClick,
      marketChunkRafRef,
      latestDetailRequestRef,
      commitDetailMarkets: setDetailMarkets,
    });
  }, [latestDetailRequestRef, marketChunkRafRef, setDetailMarkets]);

  const cancelPendingDetailWork = useCallback(({ preserveActiveDetail = false } = {}) => {
    // Preserve Crypto native exact-token lifecycle before advancing request and recovery generations.
    invalidateSportsnewFixtureDetailRequest({
      detailAbortControllerRef, latestDetailRequestRef, marketChunkRafRef,
      ...(preserveActiveDetail ? {} : { detailFixtureIdRef }),
      clearClickMark: clearDetailClickMark,
    });
    invalidateSportsnewDetailRequest({
      abortControllerRef: detailAbortControllerRef,
      latestRequestRef: latestDetailRequestRef,
      requestSeqRef: detailRequestSeqRef,
      chunkRafRef: marketChunkRafRef,
      cancelFrame: typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : null,
      recoveryGenerationRef: detailRecoveryGenerationRef,
    });
    detailLifecycleGenerationRef.current += 1;
    for (const [key, flight] of fixtureRecoveryInFlightRef.current) {
      const fixtureId = flight.fixtureId;
      const stillDisplayed = fixtureId != null && (fixturesRef.current.some(row => fxId(row) === fixtureId)
        || selectionsRef.current.some(row => String(row.match_id ?? row.matchId ?? row.fixtureId ?? row.fixture_id) === fixtureId));
      // Detail navigation owns only detail demand; retain current list/cart recovery.
      if (!stillDisplayed) { flight.controller?.abort(); fixtureRecoveryInFlightRef.current.delete(key); }
    }
  }, [
    detailAbortControllerRef, detailRecoveryGenerationRef, detailRequestSeqRef,
    fixtureRecoveryInFlightRef, fixturesRef, latestDetailRequestRef, marketChunkRafRef,
    selectionsRef, detailFixtureIdRef, detailLifecycleGenerationRef,
  ]);

  const clearFixtureDetail = useCallback(({ fixtureId, clearCache = false } = {}) => {
    const latestRequest = latestDetailRequestRef.current;
    const invalidatedFixtureId = String(
      fixtureId
      ?? detailFixtureIdRef.current
      ?? latestRequest?.fixtureId
      ?? latestRequest
      ?? ''
    );

    cancelPendingDetailWork();

    if (clearCache && invalidatedFixtureId) {
      const cachePrefix = `${invalidatedFixtureId}:`;
      for (const cacheKey of detailCacheRef.current.keys()) {
        if (cacheKey.startsWith(cachePrefix)) detailCacheRef.current.delete(cacheKey);
      }
    }
    if (invalidatedFixtureId) clearDetailClickMark(invalidatedFixtureId);

    detailFixtureIdRef.current = null;
    fixtureSequenceRef.current = null;
    setDetailFixture(null);
    setDetailMarkets([]);
    setDetailFixtureId(null);
    setDetailLoading(false);
  }, [
    cancelPendingDetailWork,
    detailCacheRef,
    detailFixtureIdRef,
    fixtureSequenceRef,
    latestDetailRequestRef,
    setDetailFixture,
    setDetailFixtureId,
    setDetailLoading,
    setDetailMarkets,
  ]);

  useEffect(() => () => cancelPendingDetailWork(), [cancelPendingDetailWork]);

  const clearActiveDetailIfSuspended = useCallback((fixtureIds) => {
    const activeFixtureId = detailFixtureIdRef.current;
    if (!shouldClearSportsnewActiveDetail(activeFixtureId, fixtureIds)) return false;
    clearFixtureDetail({ fixtureId: activeFixtureId, clearCache: true });
    return true;
  }, [clearFixtureDetail, detailFixtureIdRef]);

  const recoverActiveFixtureDetail = useCallback(({
    fixtureId,
    since,
    applyFixture = true,
    request = null,
  }) => {
    if (!SportsnewService || fixtureId == null) return false;
    const realtimeRevisionAtRequest = detailRealtimeLedgerRef.current.revision;
    const accountOwner = sessionOwnerRef.current;
    const token = beginSportsnewDetailRecovery(detailRecoveryGenerationRef, fixtureId);
    if (!token) return false;

    let recoveryRequest;
    try {
      recoveryRequest = typeof request === 'function'
        ? request()
        : SportsnewService.recoverFixture(fixtureId, {
          mode: modeRef.current,
          scope: displayScopeRef.current,
          ...(since != null ? { since } : {}),
        });
    } catch {
      return false;
    }

    Promise.resolve(recoveryRequest).then((res) => {
      if (sessionOwnerRef.current !== accountOwner) return;
      if (!isSportsnewDetailRecoveryCurrent({
        token,
        recoveryGenerationRef: detailRecoveryGenerationRef,
        detailFixtureIdRef,
        operatorSuspendedFixtureIdsRef,
      })) return;
      const recovered = recoverFixtureSnapshotPayload(res?.data);
      if (applyFixture && recovered.fixture) setDetailFixture(recovered.fixture);
      if (Array.isArray(recovered.markets)) {
        setDetailMarkets(reconcileRestDetailMarkets(recovered.markets, recovered.fixture, realtimeRevisionAtRequest, recovered.marketsPage, { authority: recovered.authority }));
      }
    }).catch(() => {});
    return true;
  }, [
    SportsnewService, detailFixtureIdRef, detailRealtimeLedgerRef, detailRecoveryGenerationRef,
    displayScopeRef, modeRef, operatorSuspendedFixtureIdsRef, reconcileRestDetailMarkets,
    sessionOwnerRef, setDetailFixture, setDetailMarkets,
  ]);

  const loadFixtureDetail = useCallback(async (fixtureId) => {
    frameDeliveryRef?.current?.flush();
    if (!SportsnewService || !fixtureId) return;

    cancelPendingDetailWork();
    const requestToken = createSportsnewDetailRequestToken({
      fixtureId,
      generation: detailRequestSeqRef.current,
      mode,
      scope: displayScope,
    });
    const { fixtureId: requestFixtureId, mode: requestMode, scope: requestScope } = requestToken;
    markDetailClick(requestFixtureId);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    detailAbortControllerRef.current = controller;
    latestDetailRequestRef.current = requestToken;
    detailFixtureIdRef.current = requestFixtureId;
    setDetailFixtureId(requestFixtureId);

    const cacheKey = `${requestFixtureId}:${requestMode}:${requestScope}`;
    const cached = detailCacheRef.current.get(cacheKey);
    const realtimeRevisionAtRequest = detailRealtimeLedgerRef.current.revision;
    const accountOwner = sessionOwnerRef.current;
    const detailGeneration = detailLifecycleGenerationRef.current;
    const isCurrentDetailRequest = () => sessionOwnerRef.current === accountOwner && isSportsnewDetailRequestCurrent({
      latestRequest: latestDetailRequestRef.current,
      request: requestToken,
      detailFixtureId: detailFixtureIdRef.current,
      mode: modeRef.current,
      scope: displayScopeRef.current,
    }) && isSportsnewFixtureDetailGenerationCurrent({
      fixtureId: requestFixtureId, generation: detailGeneration,
      detailFixtureIdRef, detailLifecycleGenerationRef,
    });
    const allowCachedDetailPaint = canPaintSportsnewDetailCache(requestToken, cached);

    if (allowCachedDetailPaint) {
      setDetailFixture(cached.fixture);
      setDetailMarkets(cached.markets);
      setDetailLoading(false);
      markPerf(`sportsnew:header:${requestFixtureId}`); measureDetailHeader(requestFixtureId);
      markPerf(`sportsnew:first_market:${requestFixtureId}`); measureFirstMarket(requestFixtureId);
      markPerf(`sportsnew:full_detail:${requestFixtureId}`); measureFullDetailLoaded(requestFixtureId);
    } else {
      const listFixture = fixturesRef.current.find((fixture) => fxId(fixture) === requestFixtureId) || null;
      setDetailFixture(listFixture);
      setDetailMarkets([]);
      setDetailLoading(true);
      markPerf(`sportsnew:header:${requestFixtureId}`); measureDetailHeader(requestFixtureId);
    }

    try {
      const marketsRes = await SportsnewService.getFixtureMarkets(
        requestFixtureId,
        { mode: requestMode, scope: requestScope },
        controller ? { signal: controller.signal } : {},
      );
      frameDeliveryRef?.current?.flush();

      if (!isCurrentDetailRequest()) return;

      if (isSportsnewDetailUnavailable(marketsRes)) {
        detailCacheRef.current.delete(cacheKey);
        clearFixtureDetail({ fixtureId: requestFixtureId, clearCache: true });
        return;
      }
      if (marketsRes?.status === false) {
        throw new Error(marketsRes?.message || 'SPORTSNEW_DETAIL_MARKETS_FAILED');
      }

      const md = marketsRes?.data;
      const fixture = md?.fixture ?? marketsRes?.fixture ?? null;
      const marketCategories = Array.isArray(md?.categories)
        ? md.categories
        : Array.isArray(marketsRes?.categories)
          ? marketsRes.categories
          : [];
      const fixtureWithMarketCategories = fixture
        ? { ...fixture, marketCategories }
        : null;
      const responseFixture = fixtureWithMarketCategories ?? fixture;
      if (!sportsnewDetailResponseMatchesRequest(fxId(responseFixture), requestToken)) {
        throw new Error('SPORTSNEW_DETAIL_FIXTURE_IDENTITY_MISMATCH');
      }

      const marketList = Array.isArray(md?.markets) ? md.markets
        : Array.isArray(md) ? md
        : Array.isArray(md?.rows) ? md.rows
        : Array.isArray(marketsRes?.markets) ? marketsRes.markets
        : Array.isArray(marketsRes?.rows) ? marketsRes.rows
        : [];
      const ambiguousOddIds = [];
      const dedupedMarkets = reconcileRestDetailMarkets(
        marketList,
        responseFixture,
        realtimeRevisionAtRequest,
        md?.marketsPage ?? marketsRes?.marketsPage,
        { onAmbiguous: id => ambiguousOddIds.push(id) },
      );
      recordSportsnewRestOdds(detailRealtimeLedgerRef.current,
        sportsnewRealtimeOddsFromFixturePatch({ markets: dedupedMarkets }), realtimeRevisionAtRequest);

      detailCacheRef.current.set(cacheKey, {
        fixture: responseFixture,
        markets: dedupedMarkets,
        cachedAt: Date.now(),
      });

      setDetailFixture(responseFixture);
      if (requestMode === 'live') {
        setDetailMarkets(dedupedMarkets);
        markPerf(`sportsnew:first_market:${requestFixtureId}`); measureFirstMarket(requestFixtureId);
        markPerf(`sportsnew:full_detail:${requestFixtureId}`); measureFullDetailLoaded(requestFixtureId);
      } else if (cached) {
        setDetailMarkets(dedupedMarkets);
      } else {
        renderMarketsProgressively(requestToken, dedupedMarkets, true);
      }
      if (ambiguousOddIds.length) recoverFixtureDetailSingleFlight(requestFixtureId, {
        snapshot: true, focusOddStateIds: ambiguousOddIds,
      });
    } catch (err) {
      if (!isCurrentDetailRequest()) return;
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      if (isSportsnewDetailUnavailable(err)) {
        detailCacheRef.current.delete(cacheKey);
        clearFixtureDetail({ fixtureId: requestFixtureId, clearCache: true });
        return;
      }
      clearDetailClickMark(requestFixtureId);
      console.error('[sportsnew] loadFixtureDetail error:', err);
    } finally {
      if (isCurrentDetailRequest()) setDetailLoading(false);
      if (detailAbortControllerRef.current === controller) {
        detailAbortControllerRef.current = null;
      }
    }
  }, [
    SportsnewService, cancelPendingDetailWork, detailRequestSeqRef, detailLifecycleGenerationRef, mode, displayScope,
    detailAbortControllerRef, latestDetailRequestRef, detailFixtureIdRef, setDetailFixtureId,
    detailCacheRef, detailRealtimeLedgerRef, sessionOwnerRef, modeRef, displayScopeRef,
    setDetailFixture, setDetailMarkets, setDetailLoading, fixturesRef, reconcileRestDetailMarkets,
    recoverFixtureDetailSingleFlight, clearFixtureDetail, renderMarketsProgressively,
  ]);

  return {
    cancelPendingDetailWork,
    clearActiveDetailIfSuspended,
    clearFixtureDetail,
    loadFixtureDetail,
    recoverActiveFixtureDetail,
  };
};
